// Web Vitals → analyticsService bridge. Reports LCP / CLS / INP / FCP / TTFB
// as `web_vital` impressions on the existing analytics pipeline so we get the
// same consent gating, queueing, and beacon-on-unload behavior as every other
// event.
//
// Notes on ordering:
//   * The metric callbacks fire on `visibilitychange → hidden` (and on
//     bfcache restore). The analytics service flushes its queue on both
//     `visibilitychange` and `pagehide`; the latter is the safety net if the
//     metric listener happens to register after the flush listener.
//   * `web-vitals` is dynamically imported so it stays out of the entry
//     chunk. Once the module resolves, the `onX` calls register their
//     PerformanceObservers synchronously — `buffered: true` under the hood
//     means we still pick up LCP / FCP that have already fired.
//   * Errors during install or per-metric reporting are absorbed and routed
//     through `logError`. A failed vitals subscription must never break the
//     user-facing app.
import { analyticsService, type AnalyticsService } from '../services/AnalyticsService';
import { logError } from './logger';

// LCP attribution is the only breakdown we currently forward — the upstream
// type has more shapes per metric (CLS/INP), but a focused LCP slice is enough
// to triage the dominant render-path cost without bloating event payloads.
type LCPAttribution = {
    target?: string;
    url?: string;
    timeToFirstByte?: number;
    resourceLoadDelay?: number;
    resourceLoadDuration?: number;
    elementRenderDelay?: number;
};

type Metric = {
    name: 'CLS' | 'FCP' | 'INP' | 'LCP' | 'TTFB';
    value: number;
    rating: 'good' | 'needs-improvement' | 'poor';
    delta: number;
    id: string;
    navigationType: string;
    attribution?: LCPAttribution | Record<string, unknown>;
};

type ReportCallback = (metric: Metric) => void;

type WebVitalsModule = {
    onLCP: (cb: ReportCallback) => void;
    onCLS: (cb: ReportCallback) => void;
    onINP: (cb: ReportCallback) => void;
    onFCP: (cb: ReportCallback) => void;
    onTTFB: (cb: ReportCallback) => void;
};

export type WebVitalsReporterDeps = {
    service?: Pick<AnalyticsService, 'track'>;
    importWebVitals?: () => Promise<WebVitalsModule>;
    logError?: (error: unknown, context?: Record<string, string>) => void;
};

let installed = false;

export async function installWebVitalsReporter(deps: WebVitalsReporterDeps = {}): Promise<void> {
    if (installed) {
        return;
    }
    installed = true;

    const service = deps.service ?? analyticsService;
    const reportError = deps.logError ?? logError;
    const importModule = deps.importWebVitals ?? defaultImport;

    let mod: WebVitalsModule;
    try {
        mod = await importModule();
    } catch (error) {
        reportError(error, { source: 'web-vitals:install' });
        return;
    }

    const report: ReportCallback = (metric) => {
        try {
            service.track({
                eventName: 'web_vital',
                eventType: 'impression',
                properties: {
                    metric: metric.name,
                    value: metric.value,
                    rating: metric.rating,
                    delta: metric.delta,
                    metricId: metric.id,
                    navigationType: metric.navigationType,
                    ...buildAttributionProperties(metric),
                },
            });
        } catch (error) {
            reportError(error, { source: 'web-vitals:report' });
        }
    };

    try {
        mod.onLCP(report);
        mod.onCLS(report);
        mod.onINP(report);
        mod.onFCP(report);
        mod.onTTFB(report);
    } catch (error) {
        reportError(error, { source: 'web-vitals:subscribe' });
    }
}

function defaultImport(): Promise<WebVitalsModule> {
    return import('web-vitals/attribution') as Promise<WebVitalsModule>;
}

// Only LCP attribution is forwarded for now — CLS/INP attributions would be
// useful but live behind different shapes and we want a focused triage event
// before we widen the payload.
function buildAttributionProperties(metric: Metric): Record<string, unknown> {
    if (metric.name !== 'LCP' || metric.attribution == null) {
        return {};
    }
    const attribution = metric.attribution as LCPAttribution;
    const out: Record<string, unknown> = {};
    if (attribution.target != null) {
        out.element = attribution.target;
    }
    if (attribution.url != null) {
        out.url = attribution.url;
    }
    if (attribution.timeToFirstByte != null) {
        out.timeToFirstByte = attribution.timeToFirstByte;
    }
    if (attribution.resourceLoadDelay != null) {
        out.resourceLoadDelay = attribution.resourceLoadDelay;
    }
    if (attribution.resourceLoadDuration != null) {
        out.resourceLoadDuration = attribution.resourceLoadDuration;
    }
    if (attribution.elementRenderDelay != null) {
        out.elementRenderDelay = attribution.elementRenderDelay;
    }
    return out;
}

export const __testing__ = {
    reset(): void {
        installed = false;
    },
};
